<template>
    <div id="map-container">
        <div class="filters">
            <h5 class="mb-2">Status</h5>
            <label>
                <input
                    type="checkbox"
                    v-model="filterUnassigned"
                    @change="filterMarkers"
                />
                Unassigned
            </label>
            <label>
                <input
                    type="checkbox"
                    v-model="filterAssigned"
                    @change="filterMarkers"
                />
                Assigned
            </label>
        </div>

        <div class="filters road-route-toggle">
            <h5 class="mb-2">Route Type</h5>
            <div class="btn-group w-100" role="group">
                <button
                    type="button"
                    class="btn btn-sm"
                    :class="!showRoadRoutes ? 'btn-primary' : 'btn-outline-primary'"
                    @click="toggleRouteType(false)"
                >
                    Crow
                </button>
                <button
                    type="button"
                    class="btn btn-sm"
                    :class="showRoadRoutes ? 'btn-primary' : 'btn-outline-primary'"
                    @click="toggleRouteType(true)"
                >
                    Road
                </button>
            </div>

            <div class="marker-key mt-3">
                <div class="marker-key-item">
                    <span class="marker-dot marker-dot-collection"></span>
                    Collection
                </div>
                <div class="marker-key-item">
                    <span class="marker-dot marker-dot-delivery"></span>
                    Delivery
                </div>
                <div class="marker-key-item">
                    <span class="marker-dot marker-dot-split"></span>
                    Split
                </div>
            </div>
        </div>

        <div id="status-title">
            <p class="mb-0">Planning Map for {{ date }}, live updating <span :class="isOnline ? 'text-success' : 'text-danger'">{{ isOnline ? 'online' : 'offline' }}</span></p>
        </div>

        <div ref="mapElement" style="height: 100%"></div>
    </div>
</template>

<style>

#map-container {
    height: 100vh;
    width: 100vw;
    position: relative;
}

.filters,
#status-title {
    position: absolute;
    background: white;
    padding: 10px;
    border-radius: 4px;
    z-index: 5;
}

.filters {
    width: 210px;
    top: 60px;
    left: 10px;
}

.filters.road-route-toggle {
    top: 190px;
}

#status-title {
    top: 10px;
    right: 10px;
}

.filters label {
    display: block;
    margin-top: 4px;
}

.marker-key {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-top: 8px;
    font-size: 12px;
    line-height: 1.2;
}

.marker-key-item {
    display: flex;
    align-items: center;
    gap: 6px;
}

.marker-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    border: 1px solid #fff;
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.15);
}

.marker-dot-collection {
    background: #28a745;
}

.marker-dot-delivery {
    background: #dc3545;
}

.marker-dot-split {
    background: #ffc107;
}

</style>

<script setup>
    import { onMounted, markRaw, reactive, ref, toRef, computed } from 'vue';
    import { Loader } from '@googlemaps/js-api-loader';
    import { echo } from "@laravel/echo-vue";

    const props = defineProps({
        date: {
            type: String,
            required: true
        },
        movements: {
            type: [Array, Object],
            default: () => []
        }
    });

    const date = toRef(props, 'date');
    const mapElement = ref(null);
    const map = ref(null);
    const routes = reactive({});
    const startMarkers = reactive({});
    const finishMarkers = reactive({});
    const startInfoWindows = reactive({});
    const finishInfoWindows = reactive({});
    const directionsService = ref(null);
    const googleMapsClasses = ref(null);
    const isOnline = ref(false);
    const routeLoadInProgress = new Set();
    const crowRouteStrokeWeight = 6;
    const crowRouteStrokeOpacity = 0.5;
    const filterAssigned = ref(true);
    const filterUnassigned = ref(true);
    const showRoadRoutes = ref(false);

    const normalizeMovements = (movements) => {
        if (Array.isArray(movements)) {
            return movements;
        }

        if (movements && typeof movements === 'object') {
            return Object.values(movements);
        }

        return [];
    };

    const localMovements = ref([...normalizeMovements(props.movements)]);
    const googleMapsApiKey = window.GOOGLE_MAPS_API_KEY;

    const loader = new Loader({
        apiKey: googleMapsApiKey || '',
        version: 'weekly',
        libraries: ['places']
    });

    const loadLibraries = async () => {
        const imports = [
            "maps",
            "core",
            "routes"
        ];

        const [maps, core, routes] = await Promise.all(
            imports.map(lib => google.maps.importLibrary(lib))
        );

        const {
            Map,
            InfoWindow,
            StyledMapType,
            Polyline
        } = maps;

        const {
            LatLng,
            LatLngBounds,
        } = core;

        const {
            DirectionsService,
        } = routes;

        return {
            Map,
            StyledMapType,
            LatLngBounds,
            Polyline,
            LatLng,
            DirectionsService,
            InfoWindow
        };
    };

    const initMap = async () => {
        googleMapsClasses.value = await loadLibraries();
        const AUTO_FIT_PADDING = { top: 90, right: 90, bottom: 90, left: 320 };
        const AUTO_FIT_MAX_ZOOM = 10;

        const {
            Map,
            LatLngBounds,
            DirectionsService,
            StyledMapType,
        } = googleMapsClasses.value;

        const styleArray = [
            {featureType:"administrative",elementType:"all",stylers:[{visibility:"off"}]},
            {featureType:"landscape",elementType:"geometry",stylers:[{visibility:"off"}]},
            {featureType:"landscape.natural",elementType:"geometry",stylers:[{visibility:"on"},{color:"#ffffff"}]},
            {featureType:"landscape.natural.landcover",elementType:"geometry",stylers:[{visibility:"off"}]},
            {featureType:"poi",elementType:"all",stylers:[{visibility:"off"}]},
            {featureType:"road",elementType:"labels.text.fill",stylers:[{color:"#a05301"}]},
            {featureType:"road",elementType:"labels.text.stroke",stylers:[{color:"#ffffff"}]},
            {featureType:"road.highway",elementType:"geometry.fill",stylers:[{color:"#a4a4a4"}]},
            {featureType:"road.highway",elementType:"geometry.stroke",stylers:[{visibility:"off"}]},
            {featureType:"road.arterial",elementType:"geometry.fill",stylers:[{color:"#cecece"},{visibility:"on"}]},
            {featureType:"road.local",elementType:"geometry.fill",stylers:[{color: "#e2e2e2"}]},
            {featureType:"transit.line",elementType:"geometry",stylers:[{visibility:"off"}]},
            {featureType:"transit.station",elementType:"geometry",stylers:[{visibility:"off"}]},
            {featureType:"water",elementType:"all",stylers:[{color:"#46bcec"}]},
            {featureType:"water",elementType:"geometry",stylers:[{color:"#dddddd"}]},
            {featureType:"water",elementType:"geometry.fill",stylers:[{color:"#c8d7d4"}]}
        ];
        const styledMapType = new StyledMapType(styleArray, { name: 'Custom Style' });

        map.value = new Map(mapElement.value, {
            mapId: 'default',
            center: {
                lat: 55.1198611,
                lng: -4.2826482
            },
            zoom: 6.5,
            fullscreenControl: false,
            scrollwheel: false,
            mapTypeControlOptions: {
                mapTypeIds: ['roadmap','styled_map']
            },
        });

        map.value.mapTypes.set('styled_map', styledMapType);
        map.value.setMapTypeId('styled_map');

        directionsService.value = new DirectionsService();

        const applyAutoFit = (bounds) => {
            if (!map.value || !bounds) {
                return;
            }

            map.value.fitBounds(bounds, AUTO_FIT_PADDING);
            google.maps.event.addListenerOnce(map.value, 'idle', () => {
                if (map.value && map.value.getZoom() > AUTO_FIT_MAX_ZOOM) {
                    map.value.setZoom(AUTO_FIT_MAX_ZOOM);
                }
            });
        };

        const bounds = new LatLngBounds();
        let hasPlottedPoints = false;

        // This creates pins for collect and delivery and a straight line for each movement
        localMovements.value.forEach(movement => {
            hasPlottedPoints = addMovementToMap(movement, bounds) || hasPlottedPoints;
        });

        if (hasPlottedPoints) {
            applyAutoFit(bounds);
        }

        filterMarkers();

        // Recenter after resize
        window.addEventListener('resize', () => {
            google.maps.event.trigger(map.value, 'resize');
            if (hasPlottedPoints) {
                applyAutoFit(bounds);
            }
        });
    };

    const filterMarkers = () => {
        localMovements.value.forEach((m) => {
            const id   = m.id;
            const show = m.driver_id
                ? filterAssigned.value
                : filterUnassigned.value;

            if (startMarkers[id]) {
                startMarkers[id].setMap(show ? map.value : null);
            }
            if (finishMarkers[id]) {
                finishMarkers[id].setMap(show ? map.value : null);
            }
            if (routes[id]) {
                routes[id].setVisible(show);
            }
        });
    };

    const getMovementSplitFlags = (movement) => {
        return {
            isCollectionSplit: localMovements.value.some(m =>
                m.job_id === movement.job_id &&
                m.id !== movement.id &&
                movement.collection_split
            ),
            isDeliverySplit: localMovements.value.some(m =>
                m.job_id === movement.job_id &&
                m.id !== movement.id &&
                movement.delivery_split
            )
        };
    };

    const getSplitMovements = (type, movement) => {
        const isCollection = type === 'Collection';
        const splitLat = parseFloat(isCollection ? movement.collection_lat : movement.delivery_lat);
        const splitLng = parseFloat(isCollection ? movement.collection_lng : movement.delivery_lng);

        const partner = localMovements.value.find(m =>
            m.job_id === movement.job_id &&
            m.id !== movement.id &&
            (
                isCollection
                    ? parseFloat(m.delivery_lat) === splitLat && parseFloat(m.delivery_lng) === splitLng
                    : parseFloat(m.collection_lat) === splitLat && parseFloat(m.collection_lng) === splitLng
            )
        );

        return {
            deliveryMovement: isCollection ? (partner || movement) : movement,
            collectionMovement: isCollection ? movement : (partner || movement)
        };
    };

    const getMarkerOptions = (movement, type, isSplit) => {
        const isCollection = type === 'Collection';
        const lat = parseFloat(isCollection ? movement.collection_lat : movement.delivery_lat);
        const lng = parseFloat(isCollection ? movement.collection_lng : movement.delivery_lng);
        const locationLabel = (isCollection
                ? movement.collection_postcode || movement.collection_locale || movement.collection_location
                : movement.delivery_postcode || movement.delivery_locale || movement.delivery_location
        ) || 'Unknown location';

        return {
            position: { lat, lng },
            title: (isSplit ? 'Split: ' : `${type}: `) + locationLabel,
            icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: isSplit ? '#ffc107' : (isCollection ? '#28a745' : '#dc3545'),
                fillOpacity: 1,
                strokeColor: '#ffffff',
                strokeWeight: 3,
                scale: 8
            }
        };
    };

    const syncMarker = (marker, movement, type, isSplit) => {
        const options = getMarkerOptions(movement, type, isSplit);
        const show = movement.driver_id ? filterAssigned.value : filterUnassigned.value;

        if (marker) {
            marker.setPosition(options.position);
            marker.setTitle(options.title);
            marker.setIcon(options.icon);
            marker.setMap(show ? map.value : null);
            return marker;
        }

        return markRaw(new google.maps.Marker({
            map: show ? map.value : null,
            position: options.position,
            title: options.title,
            icon: options.icon
        }));
    };

    const loadRoute = async (id) => {
        if (routeLoadInProgress.has(id)) {
            return;
        }

        const collection = startMarkers[id];
        const delivery = finishMarkers[id];
        const line = routes[id];

        if (!collection || !delivery || !line) return;

        routeLoadInProgress.add(id);

        // if it’s already a full directions path, just highlight it
        try {
            if (line.getPath().getLength() > 2) {
                line.setOptions({ strokeWeight: 5, strokeOpacity: 1 });
                return;
            }

            const result = await new Promise((resolve, reject) => {
                directionsService.value.route({
                    origin:      collection.position,
                    destination: delivery.position,
                    travelMode:  google.maps.TravelMode.DRIVING,
                    unitSystem:  google.maps.UnitSystem.IMPERIAL
                }, (response, status) => {
                    if (status === 'OK') {
                        resolve(response);
                    } else {
                        reject(status);
                    }
                });
            });

            const leg = result.routes[0].legs[0];
            const points = leg.steps.flatMap(step => step.lat_lngs);

            if (showRoadRoutes.value) {
                line.setPath(points);
                line.setOptions({
                    strokeWeight: 5,
                    strokeOpacity: 1
                });
            }
        } catch (status) {
            console.error('Directions request failed:', status);
        } finally {
            routeLoadInProgress.delete(id);
        }
    };

    const hasLoadedRoadPath = (id) => {
        const line = routes[id];
        return !!line && line.getPath().getLength() > 2;
    };

    const toggleRouteType = (showRoad) => {
        showRoadRoutes.value = showRoad;
        localMovements.value.forEach(m => {
            const line = routes[m.id];
            if (!line) return;

            if (showRoadRoutes.value) {
                loadRoute(m.id);
            } else {
                // Fallback to crow flies
                const { LatLng } = googleMapsClasses.value;
                const start = new LatLng(m.collection_lat, m.collection_lng);
                const end = new LatLng(m.delivery_lat, m.delivery_lng);
                line.setPath([start, end]);
                line.setOptions({ strokeWeight: crowRouteStrokeWeight, strokeOpacity: crowRouteStrokeOpacity });
            }
        });
    };

    const refreshJobOnMap = (jobId) => {
        localMovements.value.forEach(m => {
            if (m.job_id === jobId) {
                updateMovementOnMap(m);
            }
        });
    };

    const addMovementToMap = (movement, bounds = null) => {
        const id = movement.id;

        if (!movement.collection_lat || !movement.collection_lng || !movement.delivery_lat || !movement.delivery_lng) {
            return false;
        }

        const collectionLat = parseFloat(movement.collection_lat);
        const collectionLng = parseFloat(movement.collection_lng);
        const deliveryLat = parseFloat(movement.delivery_lat);
        const deliveryLng = parseFloat(movement.delivery_lng);

        if ([collectionLat, collectionLng, deliveryLat, deliveryLng].some(Number.isNaN)) {
            return false;
        }

        const {
            LatLng,
            Polyline,
            InfoWindow
        } = googleMapsClasses.value;

        const start = new LatLng(collectionLat, collectionLng);
        const end = new LatLng(deliveryLat, deliveryLng);

        if (bounds) {
            bounds.extend(start);
            bounds.extend(end);
        }

        const startWasMissing = !startMarkers[id];
        const finishWasMissing = !finishMarkers[id];

        startMarkers[id] = syncMarker(startMarkers[id], movement, 'Collection', movement.collection_split);
        finishMarkers[id] = syncMarker(finishMarkers[id], movement, 'Delivery', movement.delivery_split);

        // Line to be filled later. Assigned need to be orange, unassigned need to be red
        let path = [start, end];
        let strokeWeight = crowRouteStrokeWeight;
        let strokeOpacity = crowRouteStrokeOpacity;

        if (showRoadRoutes.value) {
            loadRoute(id);
            strokeWeight = 5;
            strokeOpacity = 1;
        }

        routes[id] = new Polyline({
            map: map.value,
            path: path,
            strokeColor: (movement.driver_id ? 'orange' : 'red'),
            strokeOpacity: strokeOpacity,
            strokeWeight: strokeWeight
        });

        const collectionInfoWindow = new InfoWindow({
            content: generateInfoWindowContent('Collection', movement, movement.collection_split),
        });
        startInfoWindows[id] = collectionInfoWindow;

        const deliveryInfoWindow = new InfoWindow({
            content: generateInfoWindowContent('Delivery', movement, movement.delivery_split),
        });
        finishInfoWindows[id] = deliveryInfoWindow;

        if (startWasMissing) {
            startMarkers[id].addListener('click', () => {
                collectionInfoWindow.open({
                    map: map.value,
                    anchor: startMarkers[id]
                });
            });
        }

        if (finishWasMissing) {
            finishMarkers[id].addListener('click', () => {
                deliveryInfoWindow.open({
                    map: map.value,
                    anchor: finishMarkers[id]
                });
            });
        }

        return true;
    };

    const generateInfoWindowContent = (type, movement, isSplit = false) => {
        const location = type === 'Collection' ? movement.collection_location : movement.delivery_location;
        const date = type === 'Collection' ? movement.collection_date : movement.delivery_date;
        const time = type === 'Collection' ? movement.collection_time : movement.delivery_time;
        const driver = movement.driver?.name ?? 'N/A';
        const split = isSplit ? 'Split' : '';

        return `<h1 class="h4 mb-2 mt-0">${split} ${type}</h1>` +
            `<p class="mb-0"><strong>Location:</strong> ${location ?? 'N/A'}</p>` +
            `<p class="mb-0"><strong>Driver:</strong> ${driver}</p>` +
            `<p class="mb-0"><strong>Date:</strong> ${date ? date.split('T')[0] : 'N/A'}</p>` +
            `<p class="mb-2"><strong>Time:</strong> ${time ?? 'N/A'}</p>`;
    };

    const updateMovementOnMap = (movement) => {

        const id = movement.id;

        if (!movement.collection_lat || !movement.collection_lng || !movement.delivery_lat || !movement.delivery_lng) {
            removeMovementFromMap(id);
            return;
        }

        const { LatLng } = googleMapsClasses.value;

        if (startMarkers[id]) {
            startMarkers[id] = syncMarker(startMarkers[id], movement, 'Collection',  movement.collection_split);
        }
        if (finishMarkers[id]) {
            finishMarkers[id] = syncMarker(finishMarkers[id], movement, 'Delivery',  movement.delivery_split);
        }

        if (startInfoWindows[id]) {
            startInfoWindows[id].setContent(generateInfoWindowContent('Collection', movement,  movement.collection_split));
        }
        if (finishInfoWindows[id]) {
            finishInfoWindows[id].setContent(generateInfoWindowContent('Delivery', movement,  movement.delivery_split));
        }

        if (routes[id]) {
            const start = new LatLng(movement.collection_lat, movement.collection_lng);
            const end = new LatLng(movement.delivery_lat, movement.delivery_lng);
            const show = movement.driver_id ? filterAssigned.value : filterUnassigned.value;

            if (showRoadRoutes.value) {
                if (!hasLoadedRoadPath(id)) {
                    routes[id].setPath([start, end]);
                    loadRoute(id);
                }

                routes[id].setOptions({
                    strokeColor: (movement.driver_id ? 'orange' : 'red'),
                    strokeWeight: 5,
                    strokeOpacity: 1,
                    visible: show
                });
            } else {
                routes[id].setPath([start, end]);
                routes[id].setOptions({
                    strokeColor: (movement.driver_id ? 'orange' : 'red'),
                    strokeWeight: crowRouteStrokeWeight,
                    strokeOpacity: crowRouteStrokeOpacity,
                    visible: show
                });
            }
        }

        filterMarkers();
    };

    const removeMovementFromMap = (id) => {
        const clearMarker = (marker) => {
            if (marker && typeof marker.setMap === 'function') {
                marker.setMap(null);
            }
        };

        if (startMarkers[id]) {
            clearMarker(startMarkers[id]);
            delete startMarkers[id];
        }
        if (finishMarkers[id]) {
            clearMarker(finishMarkers[id]);
            delete finishMarkers[id];
        }
        if (routes[id]) {
            routes[id].setMap(null);
            delete routes[id];
        }
        if (startInfoWindows[id]) {
            delete startInfoWindows[id];
        }
        if (finishInfoWindows[id]) {
            delete finishInfoWindows[id];
        }

        filterMarkers();
    };

    const subscribePlannerUpdates = () => {
        const channelName = `planner.${date.value}`;

        echo().private(channelName)
            .listen('.MovementCreated', (payload) => onMovementUpdated(payload))
            .listen('.MovementUpdated', (payload) => onMovementUpdated(payload))
            .listen('.MovementDeleted', (payload) => onMovementDeleted(payload))
            .on('pusher:subscription_succeeded', () => {
                isOnline.value = true;
            })
            .on('pusher:subscription_error', () => {
                isOnline.value = false;
            });
    };

    const onMovementUpdated = ({ model: updated }) => {
        const updatedDate = updated.collection_date ? String(updated.collection_date).slice(0, 10) : null;
        const existingIndex = localMovements.value.findIndex(m => m.id === updated.id);

        if (existingIndex !== -1) {
            if (updatedDate !== date.value) {
                // Movement moved to another date
                removeMovementFromMap(updated.id);
                localMovements.value.splice(existingIndex, 1);
            } else {
                // Update existing movement
                const oldMovement = localMovements.value[existingIndex];
                localMovements.value[existingIndex] = { ...oldMovement, ...updated };

                // Update all movements for this job to refresh split pins
                localMovements.value.forEach(m => {
                    if (m.job_id === updated.job_id) {
                        updateMovementOnMap(m);
                    }
                });

                if (showRoadRoutes.value && updated.collection_lat && updated.delivery_lat && !hasLoadedRoadPath(updated.id)) {
                    loadRoute(updated.id);
                }
            }
        } else if (updatedDate === date.value) {
            // New movement for this date
            localMovements.value.push(updated);
            addMovementToMap(updated);

            // Refresh others in same job to update split pins
            localMovements.value.forEach(m => {
                if (m.job_id === updated.job_id && m.id !== updated.id) {
                    updateMovementOnMap(m);
                }
            });

            if (showRoadRoutes.value && updated.collection_lat && updated.delivery_lat && !hasLoadedRoadPath(updated.id)) {
                loadRoute(updated.id);
            }
        }
    };

    const onMovementDeleted = ({ model: deleted }) => {
        const existingIndex = localMovements.value.findIndex(m => m.id === deleted.id);
        if (existingIndex !== -1) {
            const jobId = localMovements.value[existingIndex].job_id;
            removeMovementFromMap(deleted.id);
            localMovements.value.splice(existingIndex, 1);

            // Refresh others in same job to update split pins
            localMovements.value.forEach(m => {
                if (m.job_id === jobId) {
                    updateMovementOnMap(m);
                }
            });
        }
    };

    onMounted(async () => {
        if (!googleMapsApiKey) {
            console.error('PlannerMap: Missing GOOGLE_MAPS_API_KEY environment variable.');
            return;
        }

        await loader.load();
        await initMap();
        subscribePlannerUpdates();
    });

</script>
